/**
 * What the Refresh page shows, and exactly which slugs each button sends.
 *
 * Pure, and the page component is a renderer over it: the slug sets are the one
 * thing here that can be wrong in a way nobody sees until a run reads the wrong
 * site, so they are decided in a tested function rather than inline in JSX.
 *
 * The hard rule this file obeys: NO registry knowledge. Nothing here knows a
 * hostname, which issuer owns which program, or that Amex's halves share a host
 * while Chase's do not. All of that is in `browser_sources.yaml`, reaches here
 * through the extension's catalog, and is rendered as given. The moment this
 * file guesses any of it, Wallet can offer a button the run does not honour.
 */

import { ago, daysSince } from "../../../shared/captureFormat";
import type {
  Catalog, CatalogOfferSource, CatalogProgram, WireOfferResult, WireRunSummary,
} from "../../../shared/capturePort";

/** One card issuer: its offers page if served, its points, and the paired run. */
export interface IssuerSection {
  issuer: string;
  /**
   * The served offers source, or null for an issuer Wallet knows only through a
   * program's `issuer`. Null is Capital One: its points are captured and its
   * offers page is not served, which is the case this whole field exists for --
   * the row appears with Points and no Offers button rather than the program
   * sitting among the airlines.
   */
  offers: CatalogOfferSource | null;
  /**
   * This issuer's points programs: the ones a served source pairs with, plus any
   * the registry groups here by `issuer`. A paired slug the catalog does not
   * serve is left out of this list and still sent by `combined`, because the
   * registry is the authority on the pairing and the extension filters by
   * membership anyway.
   */
  points: CatalogProgram[];
  /**
   * The slug list for "Points + offers", or null when the registry has not
   * paired this source with anything. Null is why the button is absent rather
   * than present and dead.
   *
   * Driven by `points_slugs` ONLY, never by `issuer`. The two answer different
   * questions: one is "read these together in one run", the other is "draw these
   * under one heading", and a program grouped here that the registry has not
   * paired gets a Points button and no combined one.
   */
  combined: string[] | null;
}

export interface RefreshSections {
  issuers: IssuerSection[];
  /** Everything with a capture rule that no served offers source claims. */
  programs: CatalogProgram[];
}

/**
 * Split the catalog into the two sections the page draws.
 *
 * The grouping is derived from one fact only: whether a served offers source
 * names a program in its `points_slugs`. That is the whole of what the catalog
 * can say today, and it has a visible consequence -- Capital One has a capture
 * rule and no served offers source, so it lands under "Other programs" beside
 * the airlines. Fixing that means a new field on a program in
 * `browser_sources.yaml` (an `issuer:`), carried through the catalog. It is NOT
 * fixed by matching names here: "Capital One" matching `capital-one-offers` is a
 * registry fact inferred from a string, which is the duplication this file
 * exists to avoid.
 *
 * Issuers keep the registry's own order; the rest are sorted by name, because
 * eighteen rows in load order is a list nobody can find a program in.
 */
export function refreshSections(catalog: Catalog): RefreshSections {
  const bySlug = new Map(catalog.programs.map((p) => [p.slug, p]));
  const claimed = new Set<string>();
  const sections: IssuerSection[] = [];
  const byIssuer = new Map<string, IssuerSection>();

  // The served offers sources first, in the registry's own order.
  for (const offers of catalog.offer_sources) {
    const paired = offers.points_slugs ?? [];
    for (const slug of paired) claimed.add(slug);
    const section: IssuerSection = {
      issuer: offers.issuer,
      offers,
      points: paired.map((slug) => bySlug.get(slug))
        .filter((p): p is CatalogProgram => p !== undefined),
      combined: paired.length ? combinedCommand(offers) : null,
    };
    sections.push(section);
    byIssuer.set(offers.issuer, section);
  }

  // Then every program the registry groups under an issuer. It joins that
  // issuer's row when one exists and opens a new one when it does not, which is
  // the whole point: an issuer whose offers page is not served still gets a row.
  // The join is on the issuer NAME, declared on both sides of one registry file
  // and held together by a server test, not matched or guessed here.
  for (const program of catalog.programs) {
    if (!program.issuer || claimed.has(program.slug)) continue;
    const existing = byIssuer.get(program.issuer);
    if (existing) {
      existing.points.push(program);
    } else {
      const section: IssuerSection = {
        issuer: program.issuer, offers: null, points: [program], combined: null,
      };
      sections.push(section);
      byIssuer.set(program.issuer, section);
    }
    claimed.add(program.slug);
  }

  const programs = catalog.programs
    .filter((p) => !claimed.has(p.slug))
    .toSorted((a, b) => a.name.localeCompare(b.name));
  return { issuers: sections, programs };
}

/**
 * Run all: NO filter.
 *
 * Not a list of every slug, which is the obvious implementation and the wrong
 * one twice over. It would name a paired program twice (once on its own and once
 * through its issuer) unless this file deduplicated, which is the extension's
 * job; and it would freeze the catalog as of the moment the page loaded, so a
 * rule added server-side would be silently excluded from Run all. The absence of
 * the field means "everything you serve", decided by the half that knows.
 */
export function runAllCommand(): undefined {
  return undefined;
}

/** One loyalty program, read on its own. */
export function pointsCommand(slug: string): string[] {
  return [slug];
}

/** One issuer's offers page, and no program at all. */
export function offersCommand(slug: string): string[] {
  return [slug];
}

/**
 * Every card issuer's offers page, and no program: one run that refreshes card
 * offers alone. An empty registry gives an empty list, which the page does not
 * offer to start (an empty `start` is refused, not read as "all").
 */
export function allOffersCommand(catalog: { offer_sources: { slug: string }[] }): string[] {
  return catalog.offer_sources.map((source) => source.slug);
}

/**
 * One issuer's points and its offers, in one run.
 *
 * The offers slug LAST, which is the order a run takes them: every program, then
 * the readings POST, then the offers phase. The `Set` is defence and nothing
 * depends on it -- the server refuses a duplicate inside `points_slugs` -- but a
 * registry naming its own source slug among its points would otherwise send it
 * twice.
 */
export function combinedCommand(source: CatalogOfferSource): string[] {
  return [...new Set([...(source.points_slugs ?? []), source.slug])];
}

/**
 * Which tabs a capture run writes to, for the one line that says so.
 *
 * Stated because the control sits in global chrome, where it implies it
 * refreshes the app. It does not: a run writes balances and credits (Points),
 * card-linked offers and sign-up-bonus progress (Offers), and the Bilt milestone
 * reading plus the Bilt Cash amount (Bilt). Travel fares arrive from
 * site-checker's price cron, and Credits and Cards are never read from a browser.
 */
export const RUN_WRITES = ["Points", "Offers", "Bilt"] as const;

/**
 * How many days each cadence allows before a program is overdue.
 *
 * The registry states a cadence per program and the catalog serves it, so this
 * is the one place the WORD becomes a number. A cadence the catalog serves and
 * this map does not know falls back rather than reading as fresh: an unknown
 * schedule is a reason to look, not a reason to stay quiet.
 *
 * Slack on purpose. A monthly program read 32 days ago is not interesting, and a
 * badge that appears the instant a period rolls over is one nobody reads.
 */
export const CADENCE_DAYS: Record<string, number> = {
  monthly: 38,
  quarterly: 100,
  semiannual: 190,
  yearly: 380,
};

/** What an unknown or absent cadence allows. */
export const DEFAULT_OVERDUE_DAYS = 38;

export function overdueDays(cadence: string | null | undefined): number {
  if (!cadence) return DEFAULT_OVERDUE_DAYS;
  return CADENCE_DAYS[cadence] ?? DEFAULT_OVERDUE_DAYS;
}

/**
 * Whether a program is overdue for a read, against its own cadence.
 *
 * Never read at all is overdue: that is the state a run exists to leave, and a
 * program with no reading is the one most worth pressing.
 */
export function isOverdue(program: CatalogProgram, now: Date): boolean {
  return daysSince(program.last_as_of, now) >= overdueDays(program.cadence);
}

/**
 * Re-exported, not re-implemented: this module had the same arithmetic without
 * the rounding, so an overdue threshold moved by a day across a clock change.
 */
export { daysSince };

/**
 * Whether an offers source is overdue for a read.
 *
 * One threshold rather than a cadence, because an offers entry declares none:
 * every issuer refreshes its offers monthly, which is what the registry's
 * `cadence` says for all three served sources' paired programs anyway.
 */
export function isSourceOverdue(source: CatalogOfferSource, now: Date): boolean {
  return daysSince(source.last_read_on, now) >= DEFAULT_OVERDUE_DAYS;
}

/** How many canonical things a Run all covers, for the button's own line. */
export function runAllCounts(catalog: Catalog): { programs: number; sources: number } {
  return { programs: catalog.programs.length, sources: catalog.offer_sources.length };
}

/**
 * What went wrong on an offers source, in the words the operator can act on.
 *
 * Three different failures read as one "could not run" before this: a source that
 * threw, a source nobody signed in to, and a source that ran fine while its CARDS
 * failed. The third is the common one and the most misleading, because the issuer
 * gets blamed for a card the run could not position on.
 *
 * `detail` is the distinct reasons and codes the cards reported. They are already
 * on the wire and were simply never drawn, which is why a run could say "4 cards
 * failed" and not say `switch_target_missing` once.
 */
export function offerTrouble(
  source: WireOfferResult,
): { text: string; detail: string | null } | null {
  if (source.stopped) return null;
  if (source.error) return { text: "Could not run", detail: null };
  if (source.signed_out) return { text: "Needs sign-in", detail: null };
  const failed = source.cards.filter((c) => c.state === "failed");
  if (failed.length === 0) return addTrouble(source.adds);
  const why = [...new Set(failed.flatMap((c) => [...(c.reason ? [c.reason] : []), ...c.failures]))];
  return {
    text: `${failed.length} of ${source.cards.length} cards failed`,
    detail: why.length > 0 ? why.join(", ") : null,
  };
}

/**
 * The add step's trouble, for a source that adds for you: it stopped partway,
 * Wallet gave no plan, or a card read in part held every add back. Auto-add
 * being off is not trouble.
 */
function addTrouble(
  adds: WireOfferResult["adds"],
): { text: string; detail: string | null } | null {
  if (!adds) return null;
  if (adds.stopped) {
    return { text: `Adds stopped after ${adds.pressed} of ${adds.planned}`, detail: adds.stopped };
  }
  if (adds.held === "incomplete") {
    return { text: "Nothing added", detail: "not every card was read in full" };
  }
  if (adds.held === "plan_failed") return { text: "Nothing added", detail: "plan_failed" };
  return null;
}

/** What needs a look after a run: programs and offers sources, by name. */
export function runProblems(run: WireRunSummary): {
  programs: WireRunSummary["programs"];
  offers: WireOfferResult[];
} {
  const offers = run.offers ?? [];
  return {
    // A program or source the operator stopped is not a problem to go and look at.
    programs: run.programs.filter((p) => p.state !== "recorded" && p.state !== "stopped"),
    offers: offers.filter((s) => offerTrouble(s) !== null),
  };
}

/**
 * A finished run's one-line outcome, shared by the Refresh page and the notice
 * on every other page so the two cannot word one run differently.
 */
export function runOutcome(run: WireRunSummary): {
  text: string;
  tone: "bad" | "warn" | "good";
  names: string[];
} {
  const problems = runProblems(run);
  const names = [
    ...problems.programs.map((p) => p.name),
    ...problems.offers.map((s) => `${s.issuer} offers`),
  ];
  if (run.error) return { text: "Could not run", tone: "bad", names };
  if (run.post_failed) return { text: "Wallet did not take the readings", tone: "bad", names };
  if (names.length > 0) return { text: `${names.length} need a look`, tone: "warn", names };
  return { text: "Everything read", tone: "good", names };
}

/** "at 9:31 AM" for a run that finished today, "yesterday" or "3 days ago" otherwise. */
export function finishedWhen(iso: string, now: Date): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  // The local calendar day, never the UTC prefix of the ISO string.
  const pad = (n: number) => String(n).padStart(2, "0");
  const day = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  const when = ago(day, now);
  return when === "today"
    ? `at ${at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
    : when;
}

/** What to call each slug a run can be asked for, for the queue's one line. */
export function slugNames(catalog: Catalog): Map<string, string> {
  return new Map([
    ...catalog.programs.map((p) => [p.slug, p.name] as const),
    ...catalog.offer_sources.map((s) => [s.slug, `${s.issuer} offers`] as const),
  ]);
}

/** Whether every slug of a row's command is already queued. */
export function allQueued(queued: string[], slugs: string[]): boolean {
  return slugs.length > 0 && slugs.every((slug) => queued.includes(slug));
}
